import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createUser, deleteUser, getUserDetails, getUserList, updateUser, createSupportTicket, getUserTeamDetails } from '../API/userApi';
import type { AppDispatch, RootState } from '../store/store';
import { transformUserDetailsForDisplay, transformUserDetailsForPatch } from '../transform/userTransform';


export interface IUser {
    id?: string,
    name: string,
    description: string,
    displayName: string,
    fullyQualifiedName?: string,
    email: string,
    timezone?: string,
    isBot?: boolean,
    isAdmin?: boolean,
    updatedAt?: number,
    updatedBy?: string,
    version?: number,
    profile?: {
      images: {
        image: string
      }
    },
    href?: string,
    changeDescription?: any,
    password?: string,
    confirmPassword?: string,
    tenantId?: string,
    teams?: any[],
    roles?: string[],
    deleted?: boolean
}

interface UserState {
    userList: IUser[],
    userDetails: IUser
}

export const initialUserForm: IUser = {
  name: "",
  description: "",
  displayName: "",
  email: "",
  timezone: "",
  isBot: false,
  isAdmin: false,
  profile: {
    images: {
      image: ""
    }
  },
  password: "",
  confirmPassword: "",
  tenantId: "",
  teams: [],
  roles: []
}

// Define the initial state using that type
const initialState: UserState = {
    userList: [],
    userDetails: initialUserForm
};

export const callGetUserList = createAsyncThunk(
    'users/getUserList',
    async (args, { dispatch }) => {
        const response = await getUserList();
        dispatch(setUserList(response.data.data));
    }
);

export const callGetUserDetails = createAsyncThunk(
    'users/getUserDetails',
    async (userId: string, { dispatch }) => {
        const response = await getUserDetails(userId);
        dispatch(setUserDetails(transformUserDetailsForDisplay(response.data)));
    }
);

export const callCreateUser = createAsyncThunk(
  'users/createUser',
  async (user: IUser, { dispatch }) => {
    const response = await createUser(user);
    return response.data.id;
  }
);

export const callUpdateUser = createAsyncThunk<any, any, { state: RootState }>(
  'users/updateUser',
  async (user: IUser, { getState }) => {
    const response = await updateUser(user);
    return response.data.id;
  }
)

export const callDeleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id: string, { dispatch }) => {
    const response = await deleteUser(id);
    dispatch(callGetUserList());
    return response.data;
  }
)

export const callCreateSupportTicket = createAsyncThunk(
  'users/createSupportTicket',
  async (ticket: any, { dispatch }) => {
    const response = await createSupportTicket(ticket);
    return response.data;
  }
)

export const callGetUserTeamDetails = createAsyncThunk(
  'users/getUserTeamDetails',
  async (userId: string, { dispatch }) => {
    const response = await getUserTeamDetails(userId);
    return response.data;
  }
)

export const userSlice = createSlice({
  name: 'user',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setUserList: (state, action: PayloadAction<IUser[]>) => {
      const removeDeletedUsers = action.payload.filter(user => user.deleted !== true).sort((a, b) => a.displayName.localeCompare(b.displayName));
      state.userList = removeDeletedUsers;
    },
    setUserDetails: (state, action: PayloadAction<IUser>) => {
        state.userDetails = action.payload;
    }
  }
});

export const {
  setUserList,
  setUserDetails
} = userSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectUserList = (state: RootState): IUser[] => state.user.userList;
export const selectUserDetails = (state: RootState): IUser => state.user.userDetails;

export default userSlice.reducer;
